import { useEffect, useRef } from "react";
import { NavLink, Outlet, useNavigate, Navigate } from "react-router-dom";
import { LayoutDashboard, CalendarDays, GraduationCap, Search, LogOut, Award, QrCode, Ticket } from "lucide-react";
import { getProToken, getProUser, clearProAuth } from "@/lib/proApi";
import { toast } from "sonner";

const INACTIVITY_MS = 30 * 60 * 1000; // 30 Min Auto-Logout

const NAV = [
  { to: "/pro/dashboard", label: "Dashboard", icon: LayoutDashboard, testid: "pro-nav-dashboard" },
  { to: "/pro/calendar", label: "Kalender", icon: CalendarDays, testid: "pro-nav-calendar" },
  { to: "/pro/courses", label: "Kurse", icon: GraduationCap, testid: "pro-nav-courses" },
  { to: "/pro/platzreife", label: "Platzreife", icon: Award, testid: "pro-nav-platzreife" },
  { to: "/pro/gutscheine", label: "Gutscheine", icon: Ticket, testid: "pro-nav-vouchers" },
  { to: "/pro/links", label: "QR-Codes", icon: QrCode, testid: "pro-nav-links" },
  { to: "/pro/search", label: "Teilnehmer suchen", icon: Search, testid: "pro-nav-search" },
];

const ProLayout = () => {
  const nav = useNavigate();
  const user = getProUser();
  const timer = useRef(null);

  useEffect(() => {
    const reset = () => {
      if (timer.current) clearTimeout(timer.current);
      timer.current = setTimeout(() => {
        clearProAuth();
        toast.message("Automatisch abgemeldet (Inaktivität).");
        nav("/pro-login");
      }, INACTIVITY_MS);
    };
    const evs = ["mousemove", "keydown", "click", "touchstart", "scroll"];
    evs.forEach((e) => window.addEventListener(e, reset, { passive: true }));
    reset();
    return () => { evs.forEach((e) => window.removeEventListener(e, reset)); if (timer.current) clearTimeout(timer.current); };
  }, [nav]);

  if (!getProToken()) return <Navigate to="/pro-login" replace />;

  const logout = () => { clearProAuth(); nav("/pro-login"); };

  return (
    <div className="min-h-screen bg-[#070707] text-white" data-testid="pro-layout">
      <header className="sticky top-0 z-40 bg-[#0B0B0B] border-b border-white/10">
        <div className="max-w-7xl mx-auto px-4 flex items-center justify-between h-16">
          <div className="flex items-center gap-2">
            <span className="font-heading text-lg uppercase tracking-tight">GOLFCOACHING<span className="text-[#ff0033]">.</span></span>
            <span className="hidden sm:inline font-mono-lab text-[10px] tracking-[0.25em] text-[#ff0033] uppercase ml-2">PRO PORTAL</span>
          </div>
          <button onClick={logout} data-testid="pro-logout" className="flex items-center gap-2 text-neutral-300 hover:text-[#ff0033] transition-colors text-sm font-heading uppercase tracking-wider">
            <LogOut size={16} /> Abmelden
          </button>
        </div>
        <nav className="max-w-7xl mx-auto px-2 flex gap-1 overflow-x-auto">
          {NAV.map((n) => (
            <NavLink key={n.to} to={n.to} data-testid={n.testid}
              className={({ isActive }) => `flex items-center gap-2 px-4 py-3 text-sm font-heading uppercase tracking-wider whitespace-nowrap border-b-2 transition-colors ${isActive ? "border-[#ff0033] text-white" : "border-transparent text-neutral-400 hover:text-white"}`}>
              <n.icon size={16} /> {n.label}
            </NavLink>
          ))}
        </nav>
      </header>
      <main className="max-w-7xl mx-auto px-4 py-6 md:py-8">
        <Outlet context={{ user }} />
      </main>
    </div>
  );
};

export default ProLayout;
